import { useQuery } from '@tanstack/react-query';
import { queryClient } from './query-client';
import { documentCookie, withXsrfHeader } from './xsrf-header';

const HARNESS_BASE = '/_api/symphony-harness/v1';

export type RicosContent = Record<string, unknown>;

export interface BlogDraftEditContent {
  title: string;
  ricos: RicosContent;
}

export interface BlogDraftEditIds {
  workspaceId: string;
  taskId: string;
  draftId: string;
}

export const BLOG_DRAFT_REFUSALS = [
  'UNSUPPORTED_CONTENT',
  'STALE',
  'NOT_OWNER',
  'NOT_AVAILABLE',
  'UNKNOWN_DRAFT',
  'OTHER_KIND',
  'GOING_OUT',
  'DELIVERED',
  'TOO_LARGE',
  'EMPTY',
] as const;

export type BlogDraftRefusal = (typeof BLOG_DRAFT_REFUSALS)[number] | 'OTHER';

export type BlogDraftRefused = { status: 'refused'; code: BlogDraftRefusal; message: string };

export type BlogDraftEditResult = { status: 'saved'; draftId: string; updatedAt: string } | BlogDraftRefused | { status: 'failed' };

export type BlogDraftOpenResult =
  | { status: 'open'; title: string; ricos: RicosContent; updatedAt: string; baseUrl: string }
  | BlogDraftRefused
  | { status: 'failed' };

export const MAX_TITLE = 500;
export const MAX_RICOS_BYTES = 1_500_000;

export const EDITOR_CHANNEL = 'symphony-editor';

export type EditorMessage =
  | { type: 'symphony-editor:ready' }
  | { type: 'symphony-editor:change'; dirty: boolean }
  | { type: 'symphony-editor:content'; title: string; ricos: RicosContent; dirty: boolean };

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function readEditorMessage(value: unknown): EditorMessage | null {
  const message = record(value);
  if (message?.type === `${EDITOR_CHANNEL}:ready`) return { type: `${EDITOR_CHANNEL}:ready` };
  if (message?.type === `${EDITOR_CHANNEL}:change`) return { type: `${EDITOR_CHANNEL}:change`, dirty: message.dirty === true };
  const ricos = record(message?.ricos);
  if (message?.type === `${EDITOR_CHANNEL}:content` && typeof message.title === 'string' && ricos) {
    return { type: `${EDITOR_CHANNEL}:content`, title: message.title, ricos, dirty: message.dirty === true };
  }
  return null;
}

function scriptJson(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export interface BlogEditorSetup {
  title: string;
  ricos: RicosContent;
  baseUrl: string;
  mobile: boolean;
}

export function blogEditorGlue(setup: BlogEditorSetup): string {
  return `(function(){
var W=window,D=document,K=${JSON.stringify(EDITOR_CHANNEL)},C=${scriptJson(setup)},N=D.currentScript&&D.currentScript.nonce,port=null,ed=null;
function sn(m){m.type=K+":"+m.type;try{if(port)port.postMessage(m)}catch(e){}}
function plain(){var a=D.querySelector("article")||D.body,t=a.querySelector("h1"),b=D.createElement("div");if(!t){t=D.createElement("h1");a.prepend(t)}a.append(b);return{titleEl:t,bodyEl:b}}
function start(){var E=W.SymphonyBlogEditor;if(!E||ed)return;var p=plain();ed=E.mount({el:p.bodyEl,title:C.title,titleEl:p.titleEl,ricos:C.ricos,baseUrl:C.baseUrl,mobile:C.mobile,onChange:function(s){sn({type:"change",dirty:!!s.dirty})}});sn({type:"ready"})}
function mem(){var v={};return{getItem:function(k){return Object.prototype.hasOwnProperty.call(v,k)?v[k]:null},setItem:function(k,x){v[k]=String(x)},removeItem:function(k){delete v[k]},clear:function(){v={}},key:function(i){return Object.keys(v)[i]||null},get length(){return Object.keys(v).length}}}
function shim(){try{Object.defineProperty(D,"cookie",{configurable:true,get:function(){return""},set:function(){}})}catch(e){}["localStorage","sessionStorage"].forEach(function(n){try{W[n].length}catch(e){try{Object.defineProperty(W,n,{configurable:true,value:mem()})}catch(x){}}})}
function load(src){if(W.SymphonyBlogEditor)return start();shim();var s=D.createElement("script");s.nonce=N;s.textContent=src;D.head.appendChild(s);if(D.readyState==="loading")D.addEventListener("DOMContentLoaded",start);else start()}
function rv(m){if(!m)return;if(m.type===K+":source"&&typeof m.source==="string")load(m.source);else if(ed&&m.type===K+":get")sn({type:"content",title:ed.getTitle(),ricos:ed.getRicos(),dirty:ed.isDirty()})}
D.addEventListener("click",function(e){var t=e.target;if(t&&t.closest&&t.closest("a"))e.preventDefault()},true);
D.addEventListener("submit",function(e){e.preventDefault()},true);
addEventListener("message",function(e){if(port||e.source!==W.parent||!e.data||e.data.type!==K+":connect"||!e.ports||!e.ports[0])return;port=e.ports[0];port.onmessage=function(m){rv(m.data)}});
})();`;
}

export function blogEditorDocument(setup: BlogEditorSetup, nonce: string): string {
  return `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="referrer" content="no-referrer"><meta http-equiv="Content-Security-Policy" content="script-src 'nonce-${nonce}'; object-src 'none'; frame-src 'none'; base-uri 'none'; form-action 'none'"><style>html,body{margin:0;background:white;color:#162d3d;font-family:Madefor,"Helvetica Neue",Helvetica,Arial,sans-serif}article{max-width:780px;margin:0 auto;padding:24px 16px 80px}h1{font-size:32px;line-height:1.2;margin:0 0 24px;overflow-wrap:anywhere}</style></head><body><article><h1></h1></article><script nonce="${nonce}">${blogEditorGlue(
    setup,
  )}</script></body></html>`;
}

export function blogDraftEditTooLarge(content: BlogDraftEditContent): boolean {
  return content.title.length > MAX_TITLE || new Blob([JSON.stringify(content.ricos)]).size > MAX_RICOS_BYTES;
}

function refusal(code: string): BlogDraftRefusal {
  return (BLOG_DRAFT_REFUSALS as readonly string[]).includes(code) ? (code as BlogDraftRefusal) : 'OTHER';
}

function refused(result: Record<string, unknown>): BlogDraftRefused {
  return { status: 'refused', code: refusal(text(result.code)), message: text(result.message) };
}

function refusedIn(result: Record<string, unknown> | null): BlogDraftRefused | null {
  const answer = record(result?.refused);
  return answer && text(answer.code) ? refused(answer) : null;
}

export function readBlogDraftEditResult(body: unknown): BlogDraftEditResult {
  const result = record(body);
  const saved = record(result?.saved);
  if (saved && text(saved.draftId)) return { status: 'saved', draftId: text(saved.draftId), updatedAt: text(saved.updatedAt) };
  return refusedIn(result) ?? { status: 'failed' };
}

export function readBlogDraftOpenResult(body: unknown): BlogDraftOpenResult {
  const result = record(body);
  const refusal = refusedIn(result);
  if (refusal) return refusal;
  const ricos = record(result?.ricos);
  if (ricos && text(result?.updatedAt)) {
    return { status: 'open', title: text(result?.title), ricos, updatedAt: text(result?.updatedAt), baseUrl: text(result?.baseUrl) };
  }
  return { status: 'failed' };
}

async function post(path: string, body: unknown): Promise<unknown> {
  const res = await fetch(
    `${HARNESS_BASE}${path}`,
    withXsrfHeader(
      { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      documentCookie(),
    ),
  );
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json().catch(() => null);
}

export async function openBlogDraftEdit(ids: BlogDraftEditIds): Promise<BlogDraftOpenResult> {
  try {
    return readBlogDraftOpenResult(await post('/blog-draft-edits/open', ids));
  } catch {
    return { status: 'failed' };
  }
}

export async function saveBlogDraftEdit(
  ids: BlogDraftEditIds,
  content: BlogDraftEditContent,
  baseUpdatedAt: string,
  editSessionId?: string,
): Promise<BlogDraftEditResult> {
  try {
    return readBlogDraftEditResult(
      await post('/blog-draft-edits', {
        ...ids,
        title: content.title,
        ricos: content.ricos,
        baseUpdatedAt,
        ...(editSessionId ? { editSessionId } : {}),
      }),
    );
  } catch {
    return { status: 'failed' };
  }
}

export function refreshSavedBlogDraft(ids: BlogDraftEditIds): Promise<void> {
  const { workspaceId, taskId, draftId } = ids;
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['draft-state', workspaceId, taskId, draftId] }),
    queryClient.invalidateQueries({ queryKey: ['draft-preview', workspaceId, taskId, draftId] }),
    queryClient.invalidateQueries({ queryKey: ['draft-inline-preview', workspaceId, taskId, draftId] }),
    queryClient.invalidateQueries({ queryKey: ['blog-draft-preview', workspaceId] }),
    queryClient.invalidateQueries({ queryKey: ['blog-draft-inline-preview', workspaceId] }),
  ]).then(() => undefined);
}

export function useBlogDraftOpen(ids: BlogDraftEditIds, session: number) {
  return useQuery({
    queryKey: ['blog-draft-edit-open', ids.workspaceId, ids.taskId, ids.draftId, session],
    queryFn: () => openBlogDraftEdit(ids),
    staleTime: Infinity,
    cacheTime: 0,
    retry: false,
  });
}
